import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Vehicle } from 'types/vehicle';
import { AuctionVehicleMetadata } from 'types/auction';

interface AdditionalFee {
  id: number;
  fee_type: string;
  fee_amount: number;
  fee_payer_id: number | null;
  created_at: string;
  unaward_metadata?: AuctionVehicleMetadata | null;
}

/* ─── Fee details tooltip ────────────────────────────────────────────────── */

const StyledTooltip = withStyles({
  tooltip: {
    background: '#fff',
    border: '1px solid #CCCCCC',
    borderRadius: 4,
    boxShadow: '0px 4px 4px 0px #00000040',
    padding: 0,
    maxWidth: 280,
    opacity: 1,
    zIndex: 9999
  },
  popper: {
    opacity: 1,
    zIndex: 9999
  }
})(Tooltip);

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 4px;
`;

const PopoverTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #000;
  margin-bottom: 8px;
`;

const PopoverRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

const PopoverLabel = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #666666;
  white-space: nowrap;
`;

const PopoverValue = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #666666;
  text-align: right;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid #888;
  font-size: 9px;
  font-weight: 700;
  color: #888;
  cursor: pointer;
  margin-left: 6px;
  flex-shrink: 0;
  line-height: 1;
  user-select: none;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #3994DE;
    border-color: #3994DE;
  }
`;

const formatAmount = (amount: number) =>
  `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

interface FeeDetailsPopoverProps {
  fee: AdditionalFee;
  meta: AuctionVehicleMetadata;
}

const FeeDetailsPopover = ({ fee, meta }: FeeDetailsPopoverProps) => (
  <PopoverContent>
    <PopoverTitle>Fee details</PopoverTitle>
    <PopoverRow>
      <PopoverLabel>AuctionID</PopoverLabel>
      <PopoverValue>{meta.auction_slug}</PopoverValue>
    </PopoverRow>
    <PopoverRow>
      <PopoverLabel>Date</PopoverLabel>
      <PopoverValue>{moment(meta.date).format('MM/DD/YY')}</PopoverValue>
    </PopoverRow>
    <PopoverRow>
      <PopoverLabel>Seller Name</PopoverLabel>
      <PopoverValue>{meta.counterparty_name}</PopoverValue>
    </PopoverRow>
    <PopoverRow>
      <PopoverLabel>VIN</PopoverLabel>
      <PopoverValue>{meta.vin}</PopoverValue>
    </PopoverRow>
    <PopoverRow>
      <PopoverLabel>Description</PopoverLabel>
      <PopoverValue>{meta.vehicle_title}</PopoverValue>
    </PopoverRow>
    <PopoverRow>
      <PopoverLabel>Fee</PopoverLabel>
      <PopoverValue>{formatAmount(Number(fee.fee_amount))}</PopoverValue>
    </PopoverRow>
    <PopoverRow>
      <PopoverLabel>Fee Description</PopoverLabel>
      <PopoverValue>{fee.fee_type}</PopoverValue>
    </PopoverRow>
  </PopoverContent>
);

/* ─── Pricing block ──────────────────────────────────────────────────────── */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
`;

const TotalRow = styled(Row)`
  border-bottom: none;
`;

const Label = styled.span<{ isTotal?: boolean; isLarge?: boolean }>`
  font-size: ${({ isTotal, isLarge }) => (isTotal ? '16px' : isLarge ? '14px' : '12px')};
  font-weight: 500;
  color: #000;
`;

const Value = styled.span<{ isTotal?: boolean; isLarge?: boolean }>`
  font-size: ${({ isTotal, isLarge }) => (isTotal ? '16px' : isLarge ? '14px' : '12px')};
  font-weight: 500;
  color: #000;
`;

interface PricingBlockProps {
  vehicle: Vehicle & { vehicle_price?: number; additional_fees?: AdditionalFee[] };
}

const PricingBlock = ({ vehicle }: PricingBlockProps) => {
  const vehiclePrice = vehicle.vehicle_price ?? 0;
  const buyFee = vehicle.buy_fee?.processing_fee ?? 666;
  const transportationFee = vehicle.buy_fee?.transportation_fee ?? 666;
  const additionalFees = vehicle?.additional_fees ?? [];
  const additionalFeesTotal = additionalFees.reduce((sum, f) => sum + Number(f.fee_amount), 0);
  const totalPrice = vehiclePrice + buyFee + transportationFee + additionalFeesTotal;

  return (
    <Wrapper>
      <Row>
        <Label isLarge>Vehicle Price</Label>
        <Value isLarge>{formatAmount(vehiclePrice)}</Value>
      </Row>
      <Row>
        <Label>Buy fee</Label>
        <Value>{formatAmount(buyFee)}</Value>
      </Row>
      {additionalFees.map(fee => (
        <Row key={fee.id}>
          <Label>
            {fee.fee_type}
            {fee.unaward_metadata && (
              <StyledTooltip
                title={<FeeDetailsPopover fee={fee} meta={fee.unaward_metadata} />}
                placement="bottom-start"
              >
                <InfoIcon>i</InfoIcon>
              </StyledTooltip>
            )}
          </Label>
          <Value>{formatAmount(Number(fee.fee_amount))}</Value>
        </Row>
      ))}
      {transportationFee > 0 && (
        <Row>
          <Label>Transportation fee</Label>
          <Value>{formatAmount(transportationFee)}</Value>
        </Row>
      )}
      <TotalRow>
        <Label isTotal>Total Price</Label>
        <Value isTotal>{formatAmount(totalPrice)}</Value>
      </TotalRow>
    </Wrapper>
  );
};

export default PricingBlock;
